/*
 * WiO Build-A-Box (BYO) page client logic.
 *
 * Responsibilities:
 *   - Track per-variant quantities + selected cadence
 *   - Compute discount tier (1-2 = 10%, 3-5 = 13%, 6-8 = 17%, 9+ = 22%)
 *   - Compare BYO tier discount vs cart-$ tier ladder ($80 = 10%, $115 = 15%, $140 = 20%);
 *     show whichever is higher
 *   - On submit: resolve plan via App Proxy, ensure customer tag (best-effort),
 *     POST /cart/add.js with selling_plan + cart attributes, redirect to /cart
 *
 * Tag-gate (see vault note 15 Revisit Later):
 *   Apostle gates the 20 BYO plans with customer tag 'byo-shopper'. Storefront/
 *   Customer Account APIs cannot mutate tags. This file tries two paths:
 *     1) POST /apps/wio-apostle/tag-customer  (graceful 404 if endpoint TBD)
 *     2) Cart attribute _byo_tag_request=byo-shopper as Flow-trigger fallback
 *   Anonymous customers are bounced to /account/login first.
 *
 * No em-dashes anywhere.
 */

(function () {
  'use strict';

  const root = document.querySelector('[data-wio-byo]');
  if (!root) return;

  // ----- Discount config -----
  const BYO_TIERS = [
    { min: 9, pct: 22 },
    { min: 6, pct: 17 },
    { min: 3, pct: 13 },
    { min: 1, pct: 10 },
    { min: 0, pct: 0  }
  ];
  // Cart-$ ladder (display-only comparison; actual checkout discount comes from Apostle plan)
  const CART_LADDER = [
    { min: 14000, pct: 20 }, // $140
    { min: 11500, pct: 15 }, // $115
    { min:  8000, pct: 10 }  // $80
  ];

  function byoTierFor(count) {
    return BYO_TIERS.find(t => count >= t.min) || BYO_TIERS[BYO_TIERS.length - 1];
  }
  function nextByoTier(count) {
    // returns the next-higher tier (if any) and items needed
    const current = byoTierFor(count).pct;
    const higher = BYO_TIERS.filter(t => t.pct > current).sort((a,b) => a.pct - b.pct)[0];
    if (!higher) return null;
    return { pct: higher.pct, at_items: higher.min, need: higher.min - count };
  }
  function cartLadderFor(totalCents) {
    const hit = CART_LADDER.find(t => totalCents >= t.min);
    return hit ? hit.pct : 0;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ----- DOM refs -----
  const cards = root.querySelectorAll('[data-byo-grid] .wio-byo__card');
  const freqRadios = root.querySelectorAll('[data-byo-freq] input[type="radio"]');
  const filterBtns = root.querySelectorAll('[data-byo-filter] [data-filter]');
  const bundleBtns = root.querySelectorAll('[data-byo-suggested] [data-bundle]');
  const elCount = root.querySelector('[data-byo-count]');
  const elPct = root.querySelector('[data-byo-pct]');
  const elNext = root.querySelector('[data-byo-next]');
  const elTotal = root.querySelector('[data-byo-total]');
  const elSubmit = root.querySelector('[data-byo-submit]');
  const elMsg = root.querySelector('[data-byo-msg]');
  const customerId = root.dataset.customerId || '';
  const isAnonymous = !customerId;

  // ----- State -----
  /** Map of variant_id -> {qty, price (cents), title, image, handle} */
  const state = new Map();
  let cadence = '1mo';

  // Index cards by handle for bundle quick-add
  const cardsByHandle = new Map();
  cards.forEach(card => cardsByHandle.set(card.dataset.productHandle, card));

  // ----- Recompute + render -----
  function recompute() {
    let count = 0;
    let totalCents = 0;
    state.forEach(item => {
      count += item.qty;
      totalCents += item.qty * item.price;
    });

    const byoPct = byoTierFor(count).pct;
    const cartPct = cartLadderFor(totalCents);
    const showPct = Math.max(byoPct, cartPct); // best-of UX

    elCount.textContent = String(count);
    elPct.textContent = showPct + '%';

    const next = nextByoTier(count);
    if (count === 0) {
      elNext.textContent = 'Add 1 item to save 10%';
    } else if (next) {
      const noun = next.need === 1 ? 'item' : 'items';
      elNext.textContent = 'Add ' + next.need + ' more ' + noun + ' to save ' + next.pct + '%';
    } else {
      elNext.textContent = 'Top tier reached. Maximum savings.';
    }

    const discounted = Math.round(totalCents * (100 - byoPct) / 100);
    elTotal.textContent = money(discounted);

    elSubmit.disabled = count === 0;
  }

  function updateCardVisual(card, qty) {
    card.querySelector('[data-qty-out]').textContent = String(qty);
    card.classList.toggle('is-in-box', qty > 0);
  }

  function setQty(card, qty) {
    const variantId = card.dataset.variantId;
    if (qty <= 0) {
      state.delete(variantId);
      updateCardVisual(card, 0);
    } else {
      state.set(variantId, {
        qty: qty,
        price: parseInt(card.dataset.price, 10),
        title: card.dataset.title,
        image: card.dataset.image,
        handle: card.dataset.productHandle
      });
      updateCardVisual(card, qty);
    }
    recompute();
  }

  // ----- Wire qty buttons -----
  cards.forEach(card => {
    const dec = card.querySelector('[data-qty-dec]');
    const inc = card.querySelector('[data-qty-inc]');
    dec.addEventListener('click', () => {
      const cur = state.get(card.dataset.variantId);
      setQty(card, cur ? cur.qty - 1 : 0);
    });
    inc.addEventListener('click', () => {
      const cur = state.get(card.dataset.variantId);
      setQty(card, (cur ? cur.qty : 0) + 1);
    });
  });

  // ----- Cadence radios -----
  freqRadios.forEach(r => r.addEventListener('change', e => {
    if (e.target.checked) cadence = e.target.value;
  }));

  // ----- Category filter -----
  filterBtns.forEach(btn => btn.addEventListener('click', () => {
    const want = btn.dataset.filter;
    filterBtns.forEach(b => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
    });
    cards.forEach(card => {
      if (want === 'all') {
        card.hidden = false;
      } else {
        const tags = (card.dataset.tags || '').split(',').map(t => t.trim().toLowerCase());
        card.hidden = !tags.includes(want);
      }
    });
  }));

  // ----- Suggested bundles (add +1 each, dedupe-by-setting) -----
  bundleBtns.forEach(btn => btn.addEventListener('click', () => {
    const handles = (btn.dataset.handles || '').split(',').map(h => h.trim()).filter(Boolean);
    let added = 0;
    handles.forEach(h => {
      const card = cardsByHandle.get(h);
      if (!card) return;
      const cur = state.get(card.dataset.variantId);
      // Only bump to 1 if not already in box (don't accidentally double-add)
      if (!cur) {
        setQty(card, 1);
        added += 1;
      }
    });
    if (added === 0) {
      flashMsg('Those items are already in your box.', 'ok');
    } else {
      flashMsg('Added ' + added + ' item' + (added === 1 ? '' : 's') + ' from bundle.', 'ok');
    }
  }));

  function flashMsg(text, kind) {
    elMsg.textContent = text;
    elMsg.classList.remove('is-err', 'is-ok');
    if (kind) elMsg.classList.add('is-' + kind);
    if (kind !== 'err') {
      setTimeout(() => {
        if (elMsg.textContent === text) {
          elMsg.textContent = '';
          elMsg.classList.remove('is-ok');
        }
      }, 3500);
    }
  }

  // ----- Submit flow -----
  async function submitBox() {
    if (elSubmit.disabled) return;

    // Build line items
    const items = [];
    state.forEach((it, variantId) => {
      items.push({ id: parseInt(variantId, 10), quantity: it.qty });
    });
    if (items.length === 0) return;

    // Anonymous customer: bounce to login first, then come back
    if (isAnonymous) {
      const ret = encodeURIComponent(window.location.pathname + window.location.search);
      flashMsg('Please sign in to start your subscription.', 'ok');
      setTimeout(() => { window.location.href = '/account/login?return_to=' + ret; }, 800);
      return;
    }

    elSubmit.disabled = true;
    elSubmit.textContent = 'Building your box...';
    elMsg.textContent = '';
    elMsg.classList.remove('is-err', 'is-ok');

    try {
      // 1) Resolve plan
      const itemCount = items.reduce((s, i) => s + i.quantity, 0);
      const planResp = await fetch('/apps/wio-apostle/resolve-byo-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cadence: cadence, item_count: itemCount })
      });
      if (!planResp.ok) {
        throw new Error('Plan lookup failed (' + planResp.status + '). Please try again.');
      }
      const planData = await planResp.json();
      const planId = planData.selling_plan_id;
      if (!planId) throw new Error('No selling plan returned.');

      // 2) Best-effort tag ensure. Endpoint may not exist yet; we don't block on it.
      //    See vault note 15 Revisit Later.
      try {
        const tagResp = await fetch('/apps/wio-apostle/tag-customer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag: 'byo-shopper' })
        });
        // 404 = endpoint not deployed yet, fine; Flow path below catches it.
        if (!tagResp.ok && tagResp.status !== 404) {
          console.warn('[wio-byo] tag-customer non-2xx:', tagResp.status);
        }
      } catch (tagErr) {
        console.warn('[wio-byo] tag-customer call failed:', tagErr);
      }

      // 3) Add to cart with selling_plan + Flow-trigger cart attribute fallback
      const cartPayload = {
        items: items.map(i => ({ id: i.id, quantity: i.quantity, selling_plan: planId })),
        attributes: {
          _byo_tag_request: 'byo-shopper',
          _byo_cadence: cadence,
          _byo_item_count: String(itemCount)
        }
      };
      const cartResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartPayload)
      });
      if (!cartResp.ok) {
        let bodyText = '';
        try { bodyText = await cartResp.text(); } catch (_) {}
        throw new Error('Cart add failed (' + cartResp.status + '): ' + bodyText.slice(0, 200));
      }

      // 4) Also push cart-level attributes via /cart/update.js so they persist
      //    if Shopify ignored them on /cart/add.js (safety net for Flow path).
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: cartPayload.attributes })
        });
      } catch (_) { /* non-fatal */ }

      // 5) Off to cart
      window.location.href = '/cart';
    } catch (err) {
      console.error('[wio-byo] submit error:', err);
      flashMsg(err.message || 'Something went wrong. Please try again.', 'err');
      elSubmit.disabled = false;
      elSubmit.textContent = 'Subscribe to my box';
    }
  }

  elSubmit.addEventListener('click', submitBox);

  // Initial paint
  recompute();
})();
